


import {
  Box,
  Grid,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  Typography,
  Tooltip,
  Button
} from "@mui/material";
import React, { useEffect, useRef, useState, useMemo } from "react";
import styles from "../../../styles.module.css";
import { useDispatch, useSelector } from "react-redux";
import {
  ActionMsg,
  AddButton,
  Breadcrumb,
  CustomePopover,
  DeleteDialog,
  ExcelDownload,
  HandleCloseSnackbar,
  HandleSort,
  SearchInput,
  StyledTableCell,
  StyledTableRow,
  useMessageHandler,
} from "../../reusableComponent/Reusables";
import { addCylindercsv, editDeleteCylinder, getCylinderList } from "../../../actions/actions";
import * as XLSX from "xlsx";
import UploadFileTwoToneIcon from "@mui/icons-material/UploadFileTwoTone";
import { TextField } from "@vaadin/react-components";
import { Icon } from "@vaadin/react-components/Icon.js";
import { useNavigate } from "react-router-dom";
import cylinderMasterService from "../../../services/masters/cylinderMaster.Service";
import ReusableSnackbar from "../../reusableComponent/ReusableSnackbar";
import CustomPagination from "../../reusableComponent/CustomPagination";


const CylinderList = () => {
  const dispatch = useDispatch();
  const login_details = sessionStorage.getItem("loginUser");
  const userD = JSON.parse(login_details);
  const userInfo = userD.userInfo;

  // Redux state selectors
  const totalCount = useSelector((state) => state.cylinder.total_count || 0);
  const defaultRowsPerPage = useSelector((state) => state.cylinder.rows_per_page || 15);
  const [rows, setRows] = useState([]);
  const respMsgState = useSelector((state) => state.cylinder.message);

  // Local state
  const [isDelete, setIsDelete] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState();
  const [sortBy, setSortBy] = useState(null);
  const [searchKey, setSearchKey] = useState("");
  const [fileUploadError, setFileUploadError] = useState("");
  const [successMessage, setSuccessMessage] = useState(null);
  const [total_count, setTotalCount] = useState(null);
  
  useEffect(() => {
    setTotalCount(rows[0]?.totalCount);
  }, [rows]);
  
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: defaultRowsPerPage,
  });

  // Update rowsPerPage when defaultRowsPerPage changes
  useEffect(() => {
    setController((prev) => ({
      ...prev,
      rowsPerPage: defaultRowsPerPage,
      page: 0,
    }));
  }, [defaultRowsPerPage]);

  const fileInputRef = useRef(null);

  const fields = [
    "cylinder_no",
    "prod_code",
    "prod_description",
    "tag_no",
    "tare_wt",
    "owner",
  ];

  const labels = [
    "Cylinder No",
    "Product Code",
    "Product Description",
    "Tag No",
    "Tare Weight",
    "Owner",
  ];

  // Fetch cylinder data
  const fetchData = async () => {
    try {
      const params = {
        page: controller.page,
        size: controller.rowsPerPage,
        searchKey: searchKey,
      };
      const response = await cylinderMasterService.getCylinderList(params);
      setRows(response);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [controller.page, controller.rowsPerPage, searchKey]);

  const handleChangePage = (event, newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setController({
      ...controller,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    });
  };

  const handleSearch = async (e) => {
    const searchValue = e.target.value;
    setSearchKey(searchValue);
    setController({
      ...controller,
      page: 0,
    });
  };

  const deleteRecord = async (id) => {
    try {
      await cylinderMasterService.editDeleteCylinder({
        cyl_id: id,
        changedUpdatedValue: "delete"
      });
      setIsDelete(false);
      fetchData();
      setSuccessMessage("Record deleted successfully");
    } catch (error) {
      setSuccessMessage("Error deleting record");
    }
  };

  const handleDelete = (functions) => {
    setIsDelete(true);
    setDeleteInfo(functions);
  };

  const handleClose = (event, reason) => {
    setIsDelete(false);
    setDeleteInfo(null);
  };

  const handleUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    handleImportExcel(e);
  };

  const handleImportExcel = (e) => {
    const file = e.target.files[0];
    setFileUploadError("");
    if (!file) return;

    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(fileExtension)) {
      setFileUploadError("Please upload a valid Excel file (.xlsx, .xls, or .csv)");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          setFileUploadError("The uploaded file contains no data.");
          return;
        }

        const processedData = jsonData.map((row) => ({
          cylinder_no: row["Cylinder No"] || "",
          cylinder_sr_no: row["Cylinder Serial No"] || "",
          owner: row["Owner"] || "",
          cust_name: row["Customer Name"] || "",
          cust_id: row["Customer ID"] || null,
          prod_id: row["Product ID"] || null,
          depot_name: row["Depot Name"] || "",
          prod_code: row["Product Code"] || "",
          prod_description: row["Product Description"] || "",
          cyl_capacity: row["Capacity"] || "",
          purchase_date: row["Purchase Date"] || null,
          tare_wt: row["Tare Weight"] || "",
          water_capacity: row["Water Capacity"] || "",
          test_pressure: row["Test Pressure"] || "",
          working_pressure: row["Working Pressure"] || "",
          cylinder_manufacturer: row["Cylinder Manufacturer"] || "",
          cylinder_manufacturing_code: row["Cylinder Manufacturing Code"] || "",
          cylinder_manufacturing_date: row["Cylinder Manufacturing Date"] || null,
          cylinder_purchase_date: row["Cylinder Purchase Date"] || null,
          valve_type: row["Valve Type"] || "",
          valve_manufacturer: row["Valve Manufacturer"] || "",
          valve_manufacturing_code: row["Valve Manufacturing Code"] || "",
          valve_test_pressure: row["Valve Test Pressure"] || "",
          valve_working_pressure: row["Valve Working Pressure"] || "",
          peso_approval_no: row["PESO Approval No"] || "",
          peso_approval_date: row["PESO Approval Date"] || null,
          peso_certificate_upload: row["PESO Certificate Upload"] || "",
          hydrotest_certificate_no: row["Hydrotest Certificate No"] || "",
          hydrotest_date: row["Hydrotest Date"] || null,
          hydrotest_certificate_upload: row["Hydrotest Certificate Upload"] || "",
        }));

        await dispatch(addCylindercsv({ cylinders: processedData })).unwrap();
        setSuccessMessage(`Successfully imported ${processedData.length} records.`);
        fetchData();
      } catch (error) {
        console.error("Import error:", error);
        setFileUploadError("Failed to process the file. Please check the file format.");
      }
      e.target.value = null;
    };

    reader.readAsArrayBuffer(file);
  };

  // Memoized sorted rows
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => new Date(b.entry_date) - new Date(a.entry_date));
  }, [rows]);

  const navigate = useNavigate();

  return (
    <Box className={`${styles.cts_page}`} sx={{ padding: '0px !important', margin: '0px !important' }}>
      <Box className={`${styles.body}`}>
        <Breadcrumb routeSegments={[{ name: "Cylinder List" }]} />

        {/* Header - Title Only */}
        <Box className={`${styles.header_row}`}>
          <Typography className={`${styles.header_txt}`}>CYLINDER LIST</Typography>
        </Box>

        {/* Single Line Action Bar - Export, Upload, Search, Add New */}
        <Box className={`${styles.action_bar}`}>
          {/* Export Button - Green */}
          <Button className={`${styles.export_btn}`}>
            <ExcelDownload
              data={sortedRows}
              fields={fields}
              labels={labels}
              filename="Cylinder List"
            />
            <Typography className={styles.btn_txt} >Export</Typography>
          </Button>
          
          {/* Upload Button */}
          <Button className={`${styles.action_btn}`} onClick={handleUpload}>
            <UploadFileTwoToneIcon />
            <Typography className={styles.btn_txt}>Upload</Typography>
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept=".xlsx, .xls, .csv"
            onChange={handleFileChange}
          />
          
          {/* Search Bar - Takes all remaining space */}
          <Box className={`${styles.searchbar_containerrr}`}>
            <TextField
              placeholder="Search..."
              clearButtonVisible
              className={`${styles.searchbar}`}
              value={searchKey}
              onInput={handleSearch}
            >
              <Icon slot="prefix" icon="vaadin:search" />
            </TextField>
          </Box>
          
          {/* Add New Button - Same line, after search */}
          <Button
            variant="contained"
            onClick={() => navigate("/masters/add-cylinder")}
            className={styles.addnew_btn}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M11.0621 11.0621H6.26805C5.75002 11.0621 5.33008 11.4821 5.33008 12.0001C5.33008 12.5181 5.75002 12.938 6.26805 12.938H11.0621V17.7321C11.0621 18.2501 11.4821 18.6701 12.0001 18.6701C12.5181 18.6701 12.938 18.2501 12.938 17.7321V12.938H17.7321C18.2501 12.938 18.6701 12.5181 18.6701 12.0001C18.6701 11.4821 18.2501 11.0621 17.7321 11.0621H12.938V6.26805C12.938 5.75002 12.5181 5.33008 12.0001 5.33008C11.4821 5.33008 11.0621 5.75002 11.0621 6.26805V11.0621Z"
                fill="#ffffff"
              />
            </svg>
            <Typography className={styles.addnew_btn_txt}>Add New</Typography>
          </Button>
        </Box>

        <Paper sx={{ width: '100%', marginTop: '16px' }}>
          <TableContainer component={Paper} className={`${styles.list_tbl_container}`}>
            <Table size="small" stickyHeader>
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell
                    align="left" 
                    className={`${styles.table_head}`}
                    onClick={() => HandleSort("cylinder_no", sortBy, setSortBy, rows)}
                  >
                    Cylinder No
                    <span className={`${styles.sort_icon}`}>
                      {sortBy === "cylinder_no" ? "▲" : "▼"}
                    </span>
                  </StyledTableCell>

                  <StyledTableCell
                    align="left" 
                    className={`${styles.table_head}`}
                    onClick={() => HandleSort("prod_code", sortBy, setSortBy, rows)}
                  >
                    Product Code
                    <span className={`${styles.sort_icon}`}>
                      {sortBy === "prod_code" ? "▲" : "▼"}
                    </span>
                  </StyledTableCell>

                  <StyledTableCell
                    align="left" 
                    className={`${styles.table_head}`}
                    onClick={() => HandleSort("prod_description", sortBy, setSortBy, rows)}
                  >
                    Product Description
                    <span className={`${styles.sort_icon}`}>
                      {sortBy === "prod_description" ? "▲" : "▼"}
                    </span>
                  </StyledTableCell>

                  <StyledTableCell
                    align="left" 
                    className={`${styles.table_head}`}
                    onClick={() => HandleSort("tag_no", sortBy, setSortBy, rows)}
                  >
                    Tag No
                    <span className={`${styles.sort_icon}`}>
                      {sortBy === "tag_no" ? "▲" : "▼"}
                    </span>
                  </StyledTableCell>

                  <StyledTableCell
                    align="left" 
                    className={`${styles.table_head}`}
                    onClick={() => HandleSort("tare_wt", sortBy, setSortBy, rows)}
                  >
                    Tare Wt
                    <span className={`${styles.sort_icon}`}>
                      {sortBy === "tare_wt" ? "▲" : "▼"}
                    </span>
                  </StyledTableCell>

                  <StyledTableCell
                    align="left" 
                    className={`${styles.table_head}`}
                    onClick={() => HandleSort("owner", sortBy, setSortBy, rows)}
                  >
                    Owner
                    <span className={`${styles.sort_icon}`}>
                      {sortBy === "owner" ? "▲" : "▼"}
                    </span>
                  </StyledTableCell>

                  <StyledTableCell align="center" className={`${styles.table_head}`}>
                    Action
                  </StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody sx={{ backgroundColor: '#fff' }}>
                {Array.isArray(rows) && rows.map((row) => (
                  <StyledTableRow key={row.cyl_id}>
                    <StyledTableCell align="left" className={`${styles.list_table_body}`}>
                      {row.cylinder_no}
                    </StyledTableCell>
                    <StyledTableCell align="left" className={`${styles.list_table_body}`}>
                      {row.prod_code}
                    </StyledTableCell>
                    <StyledTableCell align="left" className={`${styles.list_table_body}`}>
                      {row.prod_description}
                    </StyledTableCell>
                    <StyledTableCell align="left" className={`${styles.list_table_body}`}>
                      {row.tag_no}
                    </StyledTableCell>
                    <StyledTableCell align="left" className={`${styles.list_table_body}`}>
                      {row.tare_wt}
                    </StyledTableCell>
                    <StyledTableCell align="left" className={`${styles.list_table_body}`}>
                      {row.owner === "TGPL" ? "TGPL" : row.cust_name}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <CustomePopover
                        row={row}
                        handleDelete={false}
                        linkTo="/masters/add-cylinder"
                        handlePrint={false}
                        handleCancel={false}
                      />
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
       <CustomPagination
  page={controller.page}
  rowsPerPage={controller.rowsPerPage}
  totalCount={total_count || 0}
  onPageChange={(newPage) => handleChangePage(null, newPage)}
/>
        </Paper>

        <DeleteDialog
          open={isDelete}
          handleClose={handleClose}
          deleteInfo={deleteInfo}
          deleteRecord={deleteRecord}
          deleteID={deleteInfo?.cyl_id || null}
          displayName={deleteInfo?.cylinder_no || ""}
        />
        <ReusableSnackbar
          open={!!successMessage}
          onClose={(event, reason) => {
            if (reason !== 'clickaway') {
              setSuccessMessage("");
            }
          }}
          message={successMessage}
          autoHideDuration={2000}
          vertical="top"
          horizontal="right"
        />
      </Box>
    </Box>
  );
};

export default CylinderList;